<template>
  <el-dialog
    :model-value="isOpen"
    width="744px"
    @opened="opened"
    @closed="closed"
    @close="$emit('close')"
  >
    <h1 class="tw-mb-8 tw-tracking-wider">
      {{ $t('adm.permits.statuses.statuses_dialog.title_new') }}
    </h1>
    <el-form
      ref="Form"
      :model="form"
      :rules="formRules"
      label-position="top"
      :disabled="isLoading"
      class="tw-flex tw-flex-col tw-gap-2 tw-mt-3"
      @submit.native.prevent="submitForm"
    >
      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          :error="errors?.name?.[0]"
          :class="form.name ? 'has-value' : ''"
          :label="$t('common.fields.name')"
          prop="name"
        >
          <el-input
            v-model="form.name"
          />
        </i-form-item>
      </div>

      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          :error="errors?.description?.[0]"
          :class="form.description ? 'has-value' : ''"
          prop="description"
          class="tw-flex-3"
        >
          <el-input
            v-model="form.description"
            type="textarea"
            :rows="5"
            :placeholder="$t('common.fields.description')"
            :maxlength="512"
          />
        </i-form-item>
      </div>
      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          :error="errors?.action_dates?.[0]"
          :class="form.action_dates ? 'has-value' : ''"
          prop="action_dates"
        >
          <MultiSelectWithSearch
            v-model="form.action_dates"
            option-object-label-key="name"
            :options="actionDatesOptions"
            :label="$t('common.fields.action_dates')"
            :handle-change="(payload) => form.action_dates = payload"
          />
        </i-form-item>
      </div>
      <div class="tw-flex tw-flex-col tw-items-start tw-gap-2 tw-min-h-10 tw-border tw-rounded-xl tw-border-gray tw-p-4">
        <span class="tw-text-gray-dark">
          {{ $t('adm.permits.statuses.statuses_dialog.action_dates_order') }}
        </span>
        <draggable
          v-if="form.action_dates.length > 0"
          :list="addedActionDates"
          item-key="id"
          class="tw-flex tw-flex-col tw-w-full tw-gap-2"
          @change="handleDragEnd"
        >
          <template
            #item="{element}"
          >
            <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-px-2 tw-py-1">
              <div>
                {{ element.name }}
              </div>
              <icon-reorder />
            </div>
          </template>
        </draggable>
      </div>
    </el-form>
    <template #footer>
      <div class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row">
        <i-button
          class="tw-w-40"
          @click="$emit('close')"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          class="tw-w-40"
          :is-loading="isLoading"
          @click="submitForm"
        >
          {{ $t('common.actions.save') }}
        </i-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import { getValidationError } from '@/helpers';
import { mapGetters } from 'vuex';
import draggable from 'vuedraggable';
import MultiSelectWithSearch from '@/components/shared/MultiSelectWithSearch.vue';

const FORM_BASE = {
  name: '', // required, maxLength: 128
  description: '', // not required, but if filled - maxLength: 512
  action_dates: [], // required
};

export default {
  name: 'AddPermitStatusDialog',
  components: { MultiSelectWithSearch, draggable },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,
      errors: {},
      form: _cloneDeep(FORM_BASE),
      formRules: {
        name: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 128, message: 'Max 128 characters', trigger: 'blur' },
        ],
        description: [
          { max: 512, message: 'Max 512 characters', trigger: 'blur' },
        ],
      },
    };
  },

  computed: {
    ...mapGetters({
      actionDatesOptions: 'v2/admin/cms/permitManagement/actionDates/selectOptions',
    }),
    addedActionDates() {
      return this.form.action_dates.map((id) => this.actionDatesOptions.find((item) => item.id === id));
    },
  },

  methods: {
    handleDragEnd(changeEvent) {
      if (!changeEvent.moved) {
        return;
      }

      const { newIndex, oldIndex } = changeEvent.moved;
      const movedItem = this.form.action_dates.splice(oldIndex, 1)[0];
      this.form.action_dates.splice(newIndex, 0, movedItem);
    },

    opened() {
      this.$refs.Form?.clearValidate();
    },
    closed() {
      this.form = _cloneDeep(FORM_BASE);
      this.$refs.Form?.clearValidate();
    },

    async submitForm() {
      this.errors = {};
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('v2/admin/cms/permitManagement/statuses/addStatus', {
          payload: this.form,
        });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });

        this.$emit('refresh');
        this.$emit('close');
      } catch (error) {
        this.errors = error.response.data.data;

        this.$message.error({
          message: this.$capitalize(error.response.data.message),
          showClose: true,
        });
      }

      this.isLoading = false;
    },
  },
};
</script>
